import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { appStorage } from '@sternum/web/services/appStorage';

export interface AttacksState {
  isPolling: boolean;
  isAttacksFiltersSidebarOpen: boolean;
  areAttackNotesVisible: boolean;
  isAttackLogRowExpandedPerId: Record<string, boolean>;
  isAttackLogsViewInContextTouched: boolean;
  resetAttackLogsId: number;
}

const getInitialState: () => AttacksState = () => {
  return {
    isPolling: appStorage.attack.getIsPolling() ?? false,
    isAttacksFiltersSidebarOpen: appStorage.attack.getIsFiltersSidebarOpen() ?? true,
    areAttackNotesVisible: appStorage.attack.getAreNotesVisible() ?? false,
    isAttackLogRowExpandedPerId: {},
    isAttackLogsViewInContextTouched: false,
    resetAttackLogsId: 1,
  };
};

export const attacksSlice = createSlice({
  name: 'attacks',
  initialState: getInitialState,
  reducers: {
    togglePollingAttacksAction(state, action: PayloadAction<boolean | undefined>) {
      state.isPolling = action.payload === undefined ? !state.isPolling : action.payload;
    },

    stopPollingAttacksAction(state) {
      state.isPolling = false;
    },

    toggleIsAttacksFiltersSidebarOpenAction(state, action: PayloadAction<boolean | undefined>) {
      state.isAttacksFiltersSidebarOpen =
        action.payload === undefined ? !state.isAttacksFiltersSidebarOpen : action.payload;
    },

    toggleAreAttackNotesVisible(state, action: PayloadAction<boolean | undefined>) {
      state.areAttackNotesVisible = action.payload === undefined ? !state.areAttackNotesVisible : action.payload;
    },

    toggleRowInAttackLogsTableAction: function (state, action: PayloadAction<{ id: string; isExpanded?: boolean }>) {
      const { id: rowId, isExpanded } = action.payload;
      const isRowExpanded = state.isAttackLogRowExpandedPerId[rowId];
      const updatedIsRowExpanded = isExpanded === undefined ? !isRowExpanded : isExpanded;

      if (updatedIsRowExpanded) {
        state.isAttackLogRowExpandedPerId[rowId] = true;
      } else {
        delete state.isAttackLogRowExpandedPerId[rowId];
      }
    },

    collapseAllRowsInAttackLogsTableAction(state) {
      state.isAttackLogRowExpandedPerId = {};
    },

    toggleIsAttackLogsViewInContextTouched(state, action: PayloadAction<boolean | undefined>) {
      state.isAttackLogsViewInContextTouched =
        action.payload === undefined ? !state.isAttackLogsViewInContextTouched : action.payload;
    },

    triggerResetAttackLogIdAction(state) {
      state.resetAttackLogsId = state.resetAttackLogsId & 31;
    },

    resetStateAction() {
      return getInitialState();
    },
  },
});

export const {
  togglePollingAttacksAction,
  stopPollingAttacksAction,
  toggleIsAttacksFiltersSidebarOpenAction,
  toggleAreAttackNotesVisible,
  toggleRowInAttackLogsTableAction,
  collapseAllRowsInAttackLogsTableAction,
  toggleIsAttackLogsViewInContextTouched,
  triggerResetAttackLogIdAction,
  resetStateAction,
} = attacksSlice.actions;
